import { useLazyQuery } from '@apollo/client';
import { enqueueSnackbar } from 'notistack';
import { useEffect } from 'react';

import { gql } from '../../../generated';

import { FullUserObject, MerchantInfo } from './types';
import { useAuth } from './use-auth';

export type UserObject = {
  userInfo: {
    user_id?: number;
    roles: string[];
    defaultRole: string;
  } | null;
  isAuth: boolean;
};

const GET_USER_INFO = gql(/* GraphQL */ `
  query GetUserBaseInfo($user_id: Int) {
    user_info(where: { user_id: { _eq: $user_id } }) {
      email
      name
    }

    merchant_user(where: { user_id: { _eq: $user_id } }) {
      merchant_id
      merchant_user_role_id
    }
  }
`);

export const useUser = (): UserObject => {
  const { check_access } = useAuth();

  /** Серверный рендеринг, считаем что пользователь не авторизован */
  if (typeof window === 'undefined') {
    return {
      userInfo: null,
      isAuth: false,
    };
  }

  const userInfo = check_access();

  if (!userInfo) {
    return {
      userInfo: null,
      isAuth: false,
    };
  }

  return {
    userInfo: {
      user_id: parseInt(userInfo.hasura['x-hasura-user-id']),
      roles: userInfo.hasura['x-hasura-allowed-roles'],
      defaultRole: userInfo.hasura['x-hasura-default-role'],
    },
    isAuth: true,
  };
};

export const useFullUser = (): FullUserObject => {
  const { userInfo, isAuth } = useUser();

  const [, { data, refetch }] = useLazyQuery(GET_USER_INFO, {
    variables: {
      user_id: userInfo?.user_id,
    },
    onError: (error) => {
      enqueueSnackbar(error.message, { variant: 'error' });
    },
  });

  useEffect(() => {
    if (isAuth) {
      refetch();
    }
  }, [isAuth, refetch]);

  if (!isAuth) {
    return {
      userInfo: null,
      isAuth: false,
      merchantInfo: null,
    };
  }

  return {
    userInfo: {
      ...userInfo,
      email: data?.user_info.at(0)?.email,
      name: data?.user_info.at(0)?.name || data?.user_info.at(0)?.email,
    },
    merchantInfo: {
      merchant_id: data?.merchant_user.at(0)?.merchant_id ?? null,
      role: data?.merchant_user.at(0)?.merchant_user_role_id as MerchantInfo['role'],
    },
    isAuth,
  };
};
